import React from "react"
import { Tag, Space, Typography, Tooltip } from "antd"
import { Icon } from "components"
import {
	getStatusColor,
	getStatusLabel,
	getExternalStatusLabel,
} from "utils/status"
import Strings from "utils/strings"
import { NotDeliveredReason, ORDER_FAILED_STATUSES } from "../types"
import { getExternalReturnState } from "../externalReturns"
import { ExternalReturnTag } from "./ExternalReturnsProgress"

function formatTime(d: Date): string {
	return d.toLocaleTimeString(undefined, {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	})
}

const { Text } = Typography

function formatDateTime(d: Date): string {
	return d.toLocaleString(undefined, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	})
}

interface AddressCardProps {
	address: any
	index?: number
	showIndex?: boolean
	compact?: boolean
	notDeliveredReasons: NotDeliveredReason[]
	untreatedOptions?: {
		hideUntreatedBoxes?: boolean
		hasUntreated?: boolean
		allUntreated?: boolean
	}
	onOrderIdClick?: (orderId: string) => void
}

const AddressCard: React.FC<AddressCardProps> = ({
	address,
	index,
	showIndex = false,
	compact = false,
	notDeliveredReasons,
	untreatedOptions = {},
	onOrderIdClick,
}) => {
	const {
		hideUntreatedBoxes = false,
		hasUntreated = false,
		allUntreated = false,
	} = untreatedOptions

	const stopType = address.stopType?.toUpperCase()
	const orderId = address.order.orderId
	const isDelivered = address.order.status === "delivered"
	const eta = address.eta
	const etaString = eta
		? new Date(eta * 1000).toLocaleTimeString(undefined, {
				hour: "2-digit",
				minute: "2-digit",
				hour12: false,
			})
		: undefined

	// Determine address status
	let addressStatus = address.order.status

	if (address.order.bundledOrders && address.order.bundledOrders.length > 0) {
		// Include the main order status in the calculations
		const allStatuses = [
			address.order.status,
			...address.order.bundledOrders.map((bo: any) => bo.status),
		]

		const allDelivered = allStatuses.every((status) => status === "delivered")
		const allFailed = allStatuses.every((status) =>
			ORDER_FAILED_STATUSES.includes(status)
		)
		const hasDelivered = allStatuses.some((status) => status === "delivered")
		const hasFailed = allStatuses.some((status) =>
			ORDER_FAILED_STATUSES.includes(status)
		)

		if (allDelivered) {
			addressStatus = "delivered"
		} else if (allFailed) {
			// Check if all failed statuses are the same as the original
			const allSameAsOriginal = allStatuses.every(
				(status) => status === address.order.status
			)

			if (allSameAsOriginal) {
				// Keep original addressStatus
				addressStatus = address.order.status
			} else {
				// Different failed statuses
				addressStatus = "mixed_failed"
			}
		} else if (hasDelivered && hasFailed) {
			addressStatus = "partial_delivered"
		}
	}

	return (
		<div
			style={{
				display: "flex",
				gap: compact ? "8px" : "12px",
				width: "100%",
			}}
		>
			{showIndex && index !== undefined && (
				<div
					style={{
						fontWeight: "bold",
						color: "#8c8c8c",
						minWidth: "24px",
					}}
				>
					{address.isDepot
						? "D"
						: hideUntreatedBoxes && allUntreated
							? "-"
							: `${index + 1}.`}
				</div>
			)}
			<div style={{ flex: 1 }}>
				{/* Header section */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginBottom: compact ? "4px" : "8px",
						flexWrap: "wrap",
						gap: "4px",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "8px",
							flexWrap: "wrap",
						}}
					>
						{address.isDepot ? (
							<div style={{ fontWeight: 600, fontSize: compact ? 13 : 14 }}>
								<Tag color="default">{Strings.routes.depot}</Tag>
							</div>
						) : (
							orderId && (
								<Text
									strong
									style={{
										fontSize: compact ? "13px" : "14px",
										cursor: onOrderIdClick ? "pointer" : "inherit",
										color: onOrderIdClick ? "#1C70D9" : "inherit",
									}}
									onClick={
										onOrderIdClick
											? (e) => {
													e?.stopPropagation()
													onOrderIdClick(orderId)
												}
											: undefined
									}
								>
									{Strings.orders.order}: {orderId}
								</Text>
							)
						)}
						{!address.isDepot &&
							address.order.bundledOrders &&
							address.order.bundledOrders.length > 0 && (
								<Tooltip
									title={
										<div style={{ maxWidth: 300 }}>
											<div style={{ fontWeight: "bold", marginBottom: 4 }}>
												{Strings.orders.bundledOrders}:
											</div>
											{address.order.bundledOrders.map(
												(bundledOrder: any, idx: number) => {
													const orderId = bundledOrder.orderId || bundledOrder
													const boxes = bundledOrder.boxes || []
													const bagId = bundledOrder.bagId

													return (
														<div
															key={idx}
															style={{
																marginBottom:
																	idx < address.order.bundledOrders.length - 1
																		? 6
																		: 0,
															}}
														>
															<div style={{ fontWeight: 500 }}>{orderId}</div>
															{boxes.length > 0 && (
																<div style={{ fontSize: 11, opacity: 0.9 }}>
																	Boxes:{" "}
																	{boxes
																		.map(
																			(b: any) =>
																				`${b.id}${bagId ? ` (${bagId})` : ""}`
																		)
																		.join(", ")}
																</div>
															)}
															{/* Status of bundled order */}
															<div
																style={{
																	fontSize: 11,
																}}
															>
																{getStatusLabel(bundledOrder.status)}
															</div>
														</div>
													)
												}
											)}
										</div>
									}
									zIndex={2000}
									getPopupContainer={(trigger) => trigger.parentElement!}
								>
									<Tag
										color="purple"
										style={{ fontSize: compact ? "11px" : "12px" }}
									>
										+{address.order.bundledOrders.length}{" "}
										{Strings.generic.bundled}
									</Tag>
								</Tooltip>
							)}
					</div>
					{hideUntreatedBoxes && hasUntreated && (
						<Tooltip
							title={
								allUntreated
									? Strings.routes.untreatedBoxesWarning
									: Strings.routes.someUntreatedBoxesWarning
							}
						>
							<Icon
								name="info-2"
								style={{
									color: "#ff9800",
									fontSize: compact ? "14px" : "16px",
									cursor: "pointer",
								}}
							/>
						</Tooltip>
					)}
				</div>

				{/* Tags section */}
				{!address.isDepot && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "4px",
							marginBottom: compact ? "4px" : "8px",
							flexWrap: "wrap",
						}}
					>
						{address.order.serviceType && (
							<Tag
								color={
									address.order.serviceType === "delivery"
										? "blue"
										: address.order.serviceType === "collection"
											? "orange"
											: "-"
								}
							>
								{address.order.serviceType === "delivery"
									? Strings.orders.stopTypes.delivery
									: address.order.serviceType === "collection"
										? Strings.orders.stopTypes.pickup
										: "-"}
							</Tag>
						)}
						{stopType === "PICKUP" && (
							<Tag color="purple">{Strings.routes.bikePickup}</Tag>
						)}
						{addressStatus && (
							<Tag color={getStatusColor(addressStatus)}>
								{getStatusLabel(addressStatus)}
							</Tag>
						)}
						{address.order.externalDelivery?.partner && (
							<Tag color="orange">
								{address.order.externalDelivery.partner
									.charAt(0)
									.toUpperCase() +
									address.order.externalDelivery.partner.slice(1)}
							</Tag>
						)}
					</div>
				)}

				{/* Address */}
				<div
					style={{
						marginBottom: compact ? "2px" : "4px",
						whiteSpace: "normal",
					}}
				>
					<Text style={{ fontSize: compact ? "12px" : "13px" }}>
						{address.address || <span style={{ color: "#aaa" }}>N/A</span>}
					</Text>
				</div>

				{/* Client Information */}
				<div
					style={{
						marginBottom: compact ? "4px" : "8px",
						whiteSpace: "normal",
					}}
				>
					{address.order.user?.name && (
						<div>
							<Text style={{ fontSize: compact ? "12px" : "13px" }}>
								{address.order.user.name}{" "}
								{address.order.user.businessName &&
									`(${address.order.user.businessName})`}
							</Text>
						</div>
					)}
					{address.order.user?.phone && (
						<div style={{ marginTop: "2px" }}>
							<Text style={{ fontSize: compact ? "12px" : "13px" }}>
								{address.order.user.phone}
							</Text>
						</div>
					)}
				</div>

				{/* Details section */}
				<Space
					size="small"
					wrap
					style={{
						fontSize: compact ? "11px" : "12px",
						color: "#8c8c8c",
					}}
				>
					{address.order.slot && (
						<span>
							<Text strong style={{ fontSize: compact ? "11px" : "12px" }}>
								{Strings.routes.slot}:
							</Text>{" "}
							{`${Math.floor(address.order.slot.from / 3600000)
								.toString()
								.padStart(
									2,
									"0"
								)}:${(Math.floor(address.order.slot.from / 60000) % 60).toString().padStart(2, "0")}`}
							-
							{`${Math.floor(address.order.slot.to / 3600000)
								.toString()
								.padStart(
									2,
									"0"
								)}:${(Math.floor(address.order.slot.to / 60000) % 60).toString().padStart(2, "0")}`}
						</span>
					)}
					<span>
						<Text strong style={{ fontSize: compact ? "11px" : "12px" }}>
							{Strings.routes.eta}:
						</Text>{" "}
						{etaString || "-"}
					</span>
					{!address.isDepot &&
						(() => {
							// Collect all boxes with their bags and treated status
							const allBoxes: Array<{
								id: string
								bagId?: string
								treated: boolean
								isBundled: boolean
							}> = []

							// Add main order boxes
							if (address.order.boxes) {
								address.order.boxes.forEach((box: any) => {
									allBoxes.push({
										id: box.id,
										bagId: address.order.bagId,
										treated: box.treated,
										isBundled: false,
									})
								})
							}

							// Add bundled order boxes
							if (address.order.bundledOrders) {
								address.order.bundledOrders.forEach((bundledOrder: any) => {
									if (bundledOrder.boxes) {
										bundledOrder.boxes.forEach((box: any) => {
											allBoxes.push({
												id: box.id,
												bagId: bundledOrder.bagId,
												treated: box.treated,
												isBundled: true,
											})
										})
									}
								})
							}

							// Group boxes by ID and track which bags are bundled
							const groupedBoxes = allBoxes.reduce(
								(acc, box) => {
									if (!acc[box.id]) {
										acc[box.id] = {
											id: box.id,
											bagIds: [],
											bundledBagIds: new Set<string>(),
											treated: box.treated,
											isBundled: false,
										}
									}
									// Mark as bundled if ANY instance is bundled
									if (box.isBundled) {
										acc[box.id].isBundled = true
									}
									if (box.bagId && !acc[box.id].bagIds.includes(box.bagId)) {
										acc[box.id].bagIds.push(box.bagId)
										if (box.isBundled) {
											acc[box.id].bundledBagIds.add(box.bagId)
										}
									}
									return acc
								},
								{} as Record<
									string,
									{
										id: string
										bagIds: string[]
										bundledBagIds: Set<string>
										treated: boolean
										isBundled: boolean
									}
								>
							)

							const boxEntries = Object.values(groupedBoxes)

							if (boxEntries.length === 0) return null

							return (
								<span>
									<Text strong style={{ fontSize: compact ? "11px" : "12px" }}>
										{Strings.orders.boxes}:
									</Text>{" "}
									{boxEntries.map((boxEntry, index) => {
										// Apply color to box ID if bundled and has no bags
										const boxIdStyle =
											boxEntry.isBundled && boxEntry.bagIds.length === 0
												? { color: "#722ed1", fontStyle: "italic" as const }
												: {}

										return (
											<span
												key={boxEntry.id}
												style={{
													textDecoration:
														hideUntreatedBoxes && !boxEntry.treated
															? "line-through"
															: "none",
													opacity:
														hideUntreatedBoxes && !boxEntry.treated ? 0.6 : 1,
													...boxIdStyle,
												}}
											>
												{boxEntry.id}
												{boxEntry.bagIds.length > 0 && (
													<span>
														{" "}
														(
														{boxEntry.bagIds.map((bagId, bagIndex) => (
															<span
																key={bagId}
																style={{
																	color: boxEntry.bundledBagIds.has(bagId)
																		? "#722ed1"
																		: "inherit",
																	fontStyle: boxEntry.bundledBagIds.has(bagId)
																		? "italic"
																		: "normal",
																}}
															>
																{bagId}
																{bagIndex < boxEntry.bagIds.length - 1
																	? ", "
																	: ""}
															</span>
														))}
														)
													</span>
												)}
												{index < boxEntries.length - 1 ? ", " : ""}
											</span>
										)
									})}
								</span>
							)
						})()}
					{isDelivered && address.order.deliveredDate && (
						<span>
							<Text
								strong
								style={{
									fontSize: compact ? "11px" : "12px",
									color: "green",
								}}
							>
								{address.order.serviceType === "collection"
									? Strings.routes.deliveredCollection
									: Strings.orders.orderDelivered}
								:
							</Text>{" "}
							<Text
								style={{ fontSize: compact ? "11px" : "12px", color: "green" }}
							>
								{formatDateTime(new Date(address.order.deliveredDate))}
							</Text>
						</span>
					)}
					{address.order.notDeliveredReason && (
						<span>
							<Text
								strong
								style={{
									fontSize: compact ? "11px" : "12px",
									color: "#ff5f5dff",
								}}
							>
								{Strings.routes.notDeliveredReason}:
							</Text>{" "}
							<Text
								style={{
									fontSize: compact ? "11px" : "12px",
									color: "#ff5f5dff",
								}}
							>
								{notDeliveredReasons.find(
									(r) =>
										r.id === address.order.notDeliveredReason.toString() ||
										r.externalCode ===
											address.order.notDeliveredReason.toString()
								)?.reason || address.order.notDeliveredReason}
							</Text>
						</span>
					)}
				</Space>
				{address.order.externalDelivery?.partner &&
					(() => {
						const flow: any[] = address.order.externalDelivery.statusFlow ?? []
						const actualPickup = flow.find(
							(e) => e.status === "pickup_complete"
						)
						const actualDropoff = flow.find(
							(e) => e.status === "delivered" || e.status === "not_delivered"
						)
						const ts = (t: { $date: string } | string) =>
							formatTime(new Date(typeof t === "string" ? t : t.$date))
						const returnState = getExternalReturnState(address.order)
						return (
							<div
								style={{
									marginTop: 6,
									display: "flex",
									flexWrap: "wrap",
									gap: "8px",
									fontSize: compact ? "11px" : "12px",
									color: "#8c8c8c",
								}}
							>
								<span>
									<Text
										strong
										style={{
											fontSize: compact ? "11px" : "12px",
											color: "#fa8c16",
										}}
									>
										{Strings.routes.externalDelivery}:
									</Text>{" "}
									<Text style={{ fontSize: compact ? "11px" : "12px" }}>
										{address.order.externalDelivery.partner
											.charAt(0)
											.toUpperCase() +
											address.order.externalDelivery.partner.slice(1)}
									</Text>
								</span>
								{address.order.externalDelivery.status && (
									<span>
										<Text
											strong
											style={{ fontSize: compact ? "11px" : "12px" }}
										>
											{Strings.routes.externalDeliveryStatus}:
										</Text>{" "}
										<Text style={{ fontSize: compact ? "11px" : "12px" }}>
											{getExternalStatusLabel(
												address.order.externalDelivery.status
											)}
										</Text>
									</span>
								)}
								<ExternalReturnTag
									state={returnState}
									style={{ fontSize: compact ? 10 : 11 }}
								/>
								{address.order.externalDelivery.courier?.name && (
									<span>
										<Text
											strong
											style={{ fontSize: compact ? "11px" : "12px" }}
										>
											{Strings.routes.externalDeliveryCourier}:
										</Text>{" "}
										<Text style={{ fontSize: compact ? "11px" : "12px" }}>
											{address.order.externalDelivery.courier.name}
											{address.order.externalDelivery.courier.phoneNumber &&
												` · ${address.order.externalDelivery.courier.phoneNumber}`}
										</Text>
									</span>
								)}
								{address.order.externalDelivery.pickupEta && (
									<span>
										<Text
											strong
											style={{ fontSize: compact ? "11px" : "12px" }}
										>
											{Strings.routes.externalDeliveryPickupEta}:
										</Text>{" "}
										<Text style={{ fontSize: compact ? "10px" : "11px" }}>
											{formatTime(
												new Date(address.order.externalDelivery.pickupEta)
											)}
										</Text>
										{actualPickup && (
											<>
												{" · "}
												<Text
													type="secondary"
													style={{ fontSize: compact ? "11px" : "12px" }}
												>
													{getExternalStatusLabel(actualPickup.status)}{" "}
													{ts(actualPickup.timestamp)}
												</Text>
											</>
										)}
									</span>
								)}
								{address.order.externalDelivery.dropoffEta && (
									<span>
										<Text
											strong
											style={{ fontSize: compact ? "11px" : "12px" }}
										>
											{Strings.routes.externalDeliveryDropoffEta}:
										</Text>{" "}
										<Text style={{ fontSize: compact ? "10px" : "11px" }}>
											{formatTime(
												new Date(address.order.externalDelivery.dropoffEta)
											)}
										</Text>
										{actualDropoff && (
											<>
												{" · "}
												<Text
													type="secondary"
													style={{ fontSize: compact ? "11px" : "12px" }}
												>
													{getExternalStatusLabel(actualDropoff.status)}{" "}
													{ts(actualDropoff.timestamp)}
												</Text>
											</>
										)}
									</span>
								)}
								{returnState === "returning" &&
									address.order.externalDelivery.returnPincode && (
										<span>
											<Text
												strong
												style={{ fontSize: compact ? "11px" : "12px" }}
											>
												{Strings.routes.externalDeliveryReturnPincode}:
											</Text>{" "}
											<Text
												strong
												style={{ fontSize: compact ? "11px" : "12px" }}
											>
												{address.order.externalDelivery.returnPincode}
											</Text>
										</span>
									)}
							</div>
						)
					})()}
			</div>
		</div>
	)
}

export default AddressCard
